import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HelpModal from '../HelpModal';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import HEXASLogo from '../assets/Logos/HEXASLogo.jpg';
import GuidedPractice01FormAudio from '../assets/assets/listening/GuidedPractice01Form.mp3';

import Class09GuidedPractice01Image from '../assets/assets/listening/Class09GuidedPractice01.jpg';

import Class09GuidedPractice02Image from '../assets/assets/listening/Class09GuidedPractice02.jpg';
import { useTextHighlight } from '../utils/highlightUtils';

const Listening_Class_02 = () => {
    const navigate = useNavigate();

    const [showHelp, setShowHelp] = useState(false);

    const questionRefs = useRef([]);
    const rightColRef = useRef(null);

    // Make sure your answers state exists
    const [answers, setAnswers] = useState(Array(10).fill(''));
    const [currentQuestion, setCurrentQuestion] = useState(1);
    const onNext = () => {
        setCurrentQuestion(prev => Math.min(prev + 1, 10));
    };

    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);

    const handleStartTest = () => {
        navigate(`/guided-practice-01-form`);
    };


    const allComments = [
        { text: "pupils help to plan menus" },
        { text: "only vegetarian food" },
        { text: "different food every week" },
        { text: "daily change in menu" },
    ];
    const [comments, setComments] = useState([...allComments]);

    const removeFromRightStack = (text) => {
        setComments((prev) => prev.filter((c) => c.text !== text));
    };

    const restoreToRightStack = (text) => {
        if (comments.find((c) => c.text === text)) return; // already there
        const original = allComments.find((c) => c.text === text);
        const originalIndex = allComments.findIndex((c) => c.text === text);
        setComments((prev) => {
            const updated = [...prev];
            updated.splice(originalIndex, 0, original);
            return updated;
        });
    };

    const handlePlayPause = () => {
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleTimeUpdate = () => {
        if (!audioRef.current) return;
        setCurrentTime(audioRef.current.currentTime);
    };

    const handleLoadedMetadata = () => {
        if (!audioRef.current) return;
        setDuration(audioRef.current.duration);
    };

    const handleSeek = (e) => {
        if (!audioRef.current) return;
        const time = parseFloat(e.target.value);
        audioRef.current.currentTime = time;
        setCurrentTime(time);
    };

    const handleVolumeChange = (e) => {
        if (!audioRef.current) return;
        const vol = parseFloat(e.target.value);
        setVolume(vol);
        audioRef.current.volume = vol;
        setIsMuted(vol === 0);
    };

    const handleMute = () => {
        if (!audioRef.current) return;
        if (isMuted) {
            audioRef.current.muted = false;
            setIsMuted(false);
            audioRef.current.volume = volume;
        } else {
            audioRef.current.muted = true;
            setIsMuted(true);
        }
    };

    // Timer state
    const [timeRemaining, setTimeRemaining] = useState(30 * 60); // 30 minutes in seconds
    const [showSeconds, setShowSeconds] = useState(false);

    // Timer effect
    useEffect(() => {
        const timer = setInterval(() => {
            setTimeRemaining(prev => {
                if (prev <= 0) {
                    clearInterval(timer);
                    navigate('/');  // Redirect to home
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [navigate]);

    // Format time function
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;

        if (showSeconds) {
            return `${mins} : ${secs.toString().padStart(2, '0')} remaining`;
        }
        return `${mins} minutes remaining`;
    };

    const GreenCheck = () => (
        <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="inline-block"
        >
            <path
                d="M7 11L9 13.5L16 7"
                stroke="#7DD77D"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );

    const highlightRef = useRef(null);
    useTextHighlight(highlightRef);

    // Unique meanings for this component
    const allMeanings02 = [
        "a childhood memory",
        "hope for the future",
        "fast movement",
        "a potential threat",
        "the power of colour",
        "the continuity of life",
        "protection of nature",
        "a confused attitude to nature",
    ];
    const [availableMeanings02, setAvailableMeanings02] = useState([...allMeanings02]);

    const removeFromRightStack02 = (text) => {
        if (!text) return;
        setAvailableMeanings02((prev) => prev.filter((item) => item !== text));
    };

    const restoreToRightStack02 = (text) => {
        if (!text) return;
        if (availableMeanings02.includes(text)) return;
        const originalIndex = allMeanings02.indexOf(text);
        if (originalIndex === -1) return;
        setAvailableMeanings02((prev) => {
            const updated = [...prev];
            updated.splice(originalIndex, 0, text);
            return updated;
        });
    };

    return (
        <div className="min-h-screen bg-white flex flex-col font-[Arial]">
            {/* Help Modal */}
            <HelpModal
                isOpen={showHelp}
                onClose={() => setShowHelp(false)}
            />

            {/* Top Bar (fixed) */}
            <div className="sticky top-0 z-30 bg-white">

                <div className="relative h-[62px] bg-[#F4C3C3] border-b border-black flex items-center justify-between px-4">
                    {/* Left + Center combined on left side */}
                    <div className="flex flex-col justify-center space-y-0.5 pl-6">
                        <span className="text-[14px] text-left text-black font-bold leading-tight">
                            HEXA'S Education
                        </span>
                        <div className="flex items-center space-x-6 text-[14px] text-black font-medium">
                            <span
                                onMouseEnter={() => setShowSeconds(true)}
                                onMouseLeave={() => setShowSeconds(false)}
                                className="cursor-pointer"
                            >
                                {formatTime(timeRemaining)}
                            </span>
                        </div>
                    </div>

                    {/* Right: Icons and Finish Button */}
                    <div className="flex items-center space-x-5">
                        <button
                            onClick={() => navigate('/')}
                            className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
                        >
                            Back Home
                        </button>
                        <button
                            onClick={() => setShowHelp(true)}
                            className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
                        >
                            Finish test
                        </button>

                        {/* Icons container with larger size and spacing */}
                        <div className="flex items-center space-x-8 pl-2 pr-2">
                            {/* WiFi Icon */}
                            <div className="w-10 h-10 flex items-center justify-center">
                                <svg className="w-9 h-9 text-black" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        fillRule="evenodd"
                                        d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 010.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.24 0 1 1 0 01-1.415-1.415 5 5 0 017.07 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>

                            {/* Notification Bell Icon */}
                            <div className="w-10 h-10 flex items-center justify-center">
                                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                                </svg>
                            </div>

                            {/* Hamburger Menu Icon */}
                            <div className="w-10 h-10 flex items-center justify-center">
                                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        fillRule="evenodd"
                                        d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>

                            {/* Pen/Edit Icon */}
                            <div className="w-10 h-10 flex items-center justify-center">
                                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="relative bg-[#F4F7FA] shadow-sm border border-black rounded-lg mx-12 mt-4 mb-0 sticky top-[32px] z-20 flex items-center justify-between">
                    {/* Left: Part Title */}
                    <div className="flex-1 px-4 py-2 text-left">
                        <h2 className="text-[23px] text-black">CD Class 03</h2>
                        <h1 className="text-[18px] text-black">CD IELTS Listening Interface Practice (Flowchart/Note Completion)

                        </h1>
                    </div>

                    {/* Center: Absolutely Positioned Logo */}
                    <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                        <img
                            src={HEXASLogo}
                            alt="HEXA'S Logo"
                            className="h-[50px] object-contain"
                        />
                    </div>

                    {/* Right: Audio Player */}
                    <div className="flex items-center justify-end w-[300px] pr-4">
                        <div className="bg-[#0E7ACD] rounded-xl flex items-center px-3 py-1 w-full max-w-[420px] h-[36px]">
                            <button onClick={handlePlayPause} className="text-white mr-2 focus:outline-none">
                                {isPlaying ? <FaPause size={14} /> : <FaPlay size={14} />}
                            </button>
                            <input
                                type="range"
                                min={0}
                                max={duration}
                                step={0.01}
                                value={currentTime}
                                onChange={handleSeek}
                                className="flex-1 mx-2 accent-blue-200 h-[4px]"
                                style={{ minWidth: 100 }}
                            />
                            <button onClick={handleMute} className="text-white mr-1 focus:outline-none">
                                {isMuted || volume === 0 ? <FaVolumeMute size={12} /> : <FaVolumeUp size={12} />}
                            </button>
                            <input
                                type="range"
                                min={0}
                                max={1}
                                step={0.01}
                                value={isMuted ? 0 : volume}
                                onChange={handleVolumeChange}
                                className="accent-blue-200 h-[4px]"
                                style={{ width: 50 }}
                            />
                            <audio
                                ref={audioRef}
                                src={GuidedPractice01FormAudio}
                                onTimeUpdate={handleTimeUpdate}
                                onLoadedMetadata={handleLoadedMetadata}
                                onEnded={() => setIsPlaying(false)}
                                onError={(e) => console.error("Audio error", e)}
                                style={{ display: 'none' }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Main content (scrollable) */}
            <div className="flex-1 flex flex-col items-center justify-start w-full">
                <div className="w-full px-12 py-2 flex justify-start">

                    <div ref={highlightRef} className="bg-white shadow-sm p-6 w-full max-w-[800px] text-left overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
                        {/* Heading and instructions */}
                        <h2 className="text-[18px] font-bold text-black mb-2">Questions 31–40</h2>
                        <p className="mb-1 text-[14px]">Complete the notes below.</p>
                        <p className="mb-4 text-[14px]">
                            Write <span className="font-bold">ONE WORD ONLY</span> for each answer.
                        </p>

                        {/* Section: Tardigrades */}
                        <div className="mb-6">
                            <h3 className="text-[16px] font-bold text-black text-center mb-4">Tardigrades</h3>

                            <div className="text-[14px] space-y-2">
                                <p>● more than 1,000 species, 0.05–1.2 millimetres long</p>

                                <p className="flex items-center">
                                    ● also known as water 'bears' (due to how they
                                    <span className="mx-2 inline-block rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-0.5" style={{ minWidth: 120 }}>
                                        <input
                                            ref={el => questionRefs.current[0] = el}
                                            type="text"
                                            placeholder="31"
                                            className="w-full text-center text-[13px] font-bold bg-transparent outline-none placeholder:opacity-100 focus:placeholder:opacity-0 placeholder-black"
                                            value={answers[0]}
                                            onChange={e => {
                                                const newAnswers = [...answers];
                                                newAnswers[0] = e.target.value;
                                                setAnswers(newAnswers);
                                            }}
                                        />
                                    </span>
                                    ) and 'moss piglets'
                                </p>

                                <h4 className="font-bold text-black mb-2 mt-4">Physical appearance</h4>

                                <p className="flex items-center">
                                    ● a
                                    <span className="mx-2 inline-block rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-0.5" style={{ minWidth: 120 }}>
                                        <input
                                            ref={el => questionRefs.current[1] = el}
                                            type="text"
                                            placeholder="32"
                                            className="w-full text-center text-[13px] font-bold bg-transparent outline-none placeholder:opacity-100 focus:placeholder:opacity-0 placeholder-black"
                                            value={answers[1]}
                                            onChange={e => {
                                                const newAnswers = [...answers];
                                                newAnswers[1] = e.target.value;
                                                setAnswers(newAnswers);
                                            }}
                                        />
                                    </span>
                                    round body and four pairs of legs
                                </p>

                                <p className="flex items-center">
                                    ● claws or
                                    <span className="mx-2 inline-block rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-0.5" style={{ minWidth: 120 }}>
                                        <input
                                            ref={el => questionRefs.current[2] = el}
                                            type="text"
                                            placeholder="33"
                                            className="w-full text-center text-[13px] font-bold bg-transparent outline-none placeholder:opacity-100 focus:placeholder:opacity-0 placeholder-black"
                                            value={answers[2]}
                                            onChange={e => {
                                                const newAnswers = [...answers];
                                                newAnswers[2] = e.target.value;
                                                setAnswers(newAnswers);
                                            }}
                                        />
                                    </span>
                                    for gripping
                                </p>

                                <p>● absence of respiratory organs</p>

                                <p className="flex items-center">
                                    ● body filled with a liquid that carries both
                                    <span className="mx-2 inline-block rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-0.5" style={{ minWidth: 120 }}>
                                        <input
                                            ref={el => questionRefs.current[3] = el}
                                            type="text"
                                            placeholder="34"
                                            className="w-full text-center text-[13px] font-bold bg-transparent outline-none placeholder:opacity-100 focus:placeholder:opacity-0 placeholder-black"
                                            value={answers[3]}
                                            onChange={e => {
                                                const newAnswers = [...answers];
                                                newAnswers[3] = e.target.value;
                                                setAnswers(newAnswers);
                                            }}
                                        />
                                    </span>
                                    and blood
                                </p>

                                <p className="flex items-center">
                                    ● mouth shaped like a
                                    <span className="mx-2 inline-block rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-0.5" style={{ minWidth: 120 }}>
                                        <input
                                            ref={el => questionRefs.current[4] = el}
                                            type="text"
                                            placeholder="35"
                                            className="w-full text-center text-[13px] font-bold bg-transparent outline-none placeholder:opacity-100 focus:placeholder:opacity-0 placeholder-black"
                                            value={answers[4]}
                                            onChange={e => {
                                                const newAnswers = [...answers];
                                                newAnswers[4] = e.target.value;
                                                setAnswers(newAnswers);
                                            }}
                                        />
                                    </span>
                                    with teeth called stylets
                                </p>

                                <h4 className="font-bold text-black mb-2 mt-4">Habitat</h4>

                                <p>● often found at the bottom of a lake or on plants</p>

                                <p className="flex items-center">
                                    ● very resilient and can exist in very low or high
                                    <span className="mx-2 inline-block rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-0.5" style={{ minWidth: 120 }}>
                                        <input
                                            ref={el => questionRefs.current[5] = el}
                                            type="text"
                                            placeholder="36"
                                            className="w-full text-center text-[13px] font-bold bg-transparent outline-none placeholder:opacity-100 focus:placeholder:opacity-0 placeholder-black"
                                            value={answers[5]}
                                            onChange={e => {
                                                const newAnswers = [...answers];
                                                newAnswers[5] = e.target.value;
                                                setAnswers(newAnswers);
                                            }}
                                        />
                                    </span>
                                </p>

                                <h4 className="font-bold text-black mb-2 mt-4">Cryptobiosis</h4>

                                <p>● In dry conditions, they roll into a ball called a 'tun'.</p>

                                <p>● They stay alive with a much lower metabolism than usual.</p>

                                <p className="flex items-center">
                                    ● A type of
                                    <span className="mx-2 inline-block rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-0.5" style={{ minWidth: 120 }}>
                                        <input
                                            ref={el => questionRefs.current[6] = el}
                                            type="text"
                                            placeholder="37"
                                            className="w-full text-center text-[13px] font-bold bg-transparent outline-none placeholder:opacity-100 focus:placeholder:opacity-0 placeholder-black"
                                            value={answers[6]}
                                            onChange={e => {
                                                const newAnswers = [...answers];
                                                newAnswers[6] = e.target.value;
                                                setAnswers(newAnswers);
                                            }}
                                        />
                                    </span>
                                    ensures their DNA is not damaged.
                                </p>

                                <p className="flex items-center">
                                    ● Research is underway to find out how many days they can stay alive in
                                    <span className="mx-2 inline-block rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-0.5" style={{ minWidth: 120 }}>
                                        <input
                                            ref={el => questionRefs.current[7] = el}
                                            type="text"
                                            placeholder="38"
                                            className="w-full text-center text-[13px] font-bold bg-transparent outline-none placeholder:opacity-100 focus:placeholder:opacity-0 placeholder-black"
                                            value={answers[7]}
                                            onChange={e => {
                                                const newAnswers = [...answers];
                                                newAnswers[7] = e.target.value;
                                                setAnswers(newAnswers);
                                            }}
                                        />
                                    </span>
                                    .
                                </p>

                                <h4 className="font-bold text-black mb-2 mt-4">Feeding</h4>

                                <p className="flex items-center">
                                    ● consume liquids, e.g., those found in moss or
                                    <span className="mx-2 inline-block rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-0.5" style={{ minWidth: 120 }}>
                                        <input
                                            ref={el => questionRefs.current[8] = el}
                                            type="text"
                                            placeholder="39"
                                            className="w-full text-center text-[13px] font-bold bg-transparent outline-none placeholder:opacity-100 focus:placeholder:opacity-0 placeholder-black"
                                            value={answers[8]}
                                            onChange={e => {
                                                const newAnswers = [...answers];
                                                newAnswers[8] = e.target.value;
                                                setAnswers(newAnswers);
                                            }}
                                        />
                                    </span>
                                </p>

                                <p>● may eat other tardigrades</p>

                                <h4 className="font-bold text-black mb-2 mt-4">Conservation status</h4>

                                <p className="flex items-center">
                                    ● They are not considered to be
                                    <span className="mx-2 inline-block rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-0.5" style={{ minWidth: 120 }}>
                                        <input
                                            ref={el => questionRefs.current[9] = el}
                                            type="text"
                                            placeholder="40"
                                            className="w-full text-center text-[13px] font-bold bg-transparent outline-none placeholder:opacity-100 focus:placeholder:opacity-0 placeholder-black"
                                            value={answers[9]}
                                            onChange={e => {
                                                const newAnswers = [...answers];
                                                newAnswers[9] = e.target.value;
                                                setAnswers(newAnswers);
                                            }}
                                        />
                                    </span>
                                    .
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main content (scrollable) */}
            <div className="flex-1 flex flex-col items-center justify-start w-full">
                <div className="w-full px-12 py-2 flex justify-start">


                    <div ref={highlightRef} className="bg-white  shadow-sm p-6 w-full max-w-[800px] text-left overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
                        {/* Questions 16–18 Heading */}
                        <h2 className="text-[18px] font-bold text-black mb-2">Questions 16 – 18</h2>
                        <p className="text-[14px] mb-0">What comment does the speaker make about each of the following serving points in the Food Hall?</p>
                        <p className="text-[14px] mb-4">Choose <strong>THREE</strong> answers from the box and write the correct letter, <strong>A–D</strong>, next to Questions 16–18.</p>

                        {/* Grid layout */}
                        <div className="grid grid-cols-2 gap-6 mb-8">
                            {/* Left Column - Questions */}
                            <div>
                                <h3 className="text-[16px] font-bold text-black mb-4">
                                    Food available at serving points in Food Hall
                                </h3>
                                <div className="space-y-4">
                                    {[15, 16, 17].map((qIndex, idx) => (
                                        <div
                                            key={qIndex}
                                            className="flex items-center justify-between"
                                        >
                                            <div className="text-black font-semibold">
                                                {["World Adventures", "Street Life", "Speedy Italian"][idx]}
                                            </div>
                                            <span
                                                className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] px-2 py-1 ml-4 cursor-pointer"
                                                style={{ minWidth: 200, minHeight: 26 }}
                                                // ✅ Allow dropping full sentences
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    const droppedText = e.dataTransfer.getData("text/plain");

                                                    const newAnswers = [...answers];
                                                    const prevValue = newAnswers[qIndex];

                                                    // Restore previous value if existed
                                                    if (prevValue && prevValue !== droppedText) {
                                                        restoreToRightStack02(prevValue);
                                                    }

                                                    newAnswers[qIndex] = droppedText;
                                                    setAnswers(newAnswers);

                                                    // Remove dropped comment from right stack
                                                    removeFromRightStack02(droppedText);
                                                }}
                                                onDragOver={(e) => e.preventDefault()}
                                                // ✅ On click/touch: return value to right stack
                                                onClick={() => {
                                                    const currentValue = answers[qIndex];
                                                    if (currentValue) {
                                                        restoreToRightStack02(currentValue);
                                                        const newAnswers = [...answers];
                                                        newAnswers[qIndex] = "";
                                                        setAnswers(newAnswers);
                                                    }
                                                }}
                                            >
                                                <div className="w-full h-full bg-transparent text-center text-[13px] font-bold outline-none">
                                                    {answers[qIndex] || (
                                                        <span className="text-gray-500"></span>
                                                    )}
                                                </div>
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Right Column - Comments */}
                            <div
                                onDrop={(e) => {
                                    e.preventDefault();
                                    const text = e.dataTransfer.getData("text/plain");
                                    restoreToRightStack02(text);

                                    // Remove from any left box it was in
                                    const newAnswers = answers.map((ans) =>
                                        ans === text ? "" : ans
                                    );
                                    setAnswers(newAnswers);
                                }}
                                onDragOver={(e) => e.preventDefault()}
                            >
                                <h3 className="text-[16px] font-bold text-black mb-4">Comments</h3>
                                <div className="space-y-2 min-h-[150px]">
                                    {comments.map((item) => (
                                        <div
                                            key={item.text}
                                            draggable
                                            onDragStart={(e) =>
                                                e.dataTransfer.setData("text/plain", item.text)
                                            }
                                            className="cursor-move bg-white hover:bg-gray-100 border border-gray-300 rounded px-3 py-2 shadow text-[14px] font-medium"
                                        >
                                            {item.text}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>





                        {/* Questions 19 and 20 Section */}
                        <h2 className="text-[18px] font-bold text-black mb-2">Questions 19 and 20</h2>
                        <p className="text-[14px] mb-1">Choose <strong>TWO</strong> letters, A–E.</p>
                        <p className="text-[14px] mb-4">Which TWO optional after-school lessons are new?</p>

                        <div className="space-y-2 text-[14px]">
                            {/* A */}
                            <label className="flex items-center space-x-2 cursor-pointer text-black font-normal">
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 text-blue-600 bg-white border border-gray-400 rounded-sm focus:ring-0"
                                    checked={answers[18]?.includes("A") || false}
                                    onChange={(e) => {
                                        const current = answers[18] || [];
                                        const newAnswers = [...answers];
                                        if (e.target.checked) {
                                            newAnswers[18] = [...current, "A"].slice(0, 2);
                                        } else {
                                            newAnswers[18] = current.filter(opt => opt !== "A");
                                        }
                                        setAnswers(newAnswers);
                                    }}
                                    ref={el => questionRefs.current[18] = el}
                                />
                                <span>swimming</span>
                            </label>

                            {/* B */}
                            <label className="flex items-center space-x-2 cursor-pointer text-black font-normal">
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 text-blue-600 bg-white border border-gray-400 rounded-sm focus:ring-0"
                                    checked={answers[18]?.includes("B") || false}
                                    onChange={(e) => {
                                        const current = answers[18] || [];
                                        const newAnswers = [...answers];
                                        if (e.target.checked) {
                                            newAnswers[18] = [...current, "B"].slice(0, 2);
                                        } else {
                                            newAnswers[18] = current.filter(opt => opt !== "B");
                                        }
                                        setAnswers(newAnswers);
                                    }}
                                />
                                <span>piano</span>
                            </label>

                            {/* C */}
                            <label className="flex items-center space-x-2 cursor-pointer text-black font-normal">
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 text-blue-600 bg-white border border-gray-400 rounded-sm focus:ring-0"
                                    checked={answers[18]?.includes("C") || false}
                                    onChange={(e) => {
                                        const current = answers[18] || [];
                                        const newAnswers = [...answers];
                                        if (e.target.checked) {
                                            newAnswers[18] = [...current, "C"].slice(0, 2);
                                        } else {
                                            newAnswers[18] = current.filter(opt => opt !== "C");
                                        }
                                        setAnswers(newAnswers);
                                    }}
                                />
                                <span>acting</span>
                            </label>

                            {/* D */}
                            <label className="flex items-center space-x-2 cursor-pointer text-black font-normal">
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 text-blue-600 bg-white border border-gray-400 rounded-sm focus:ring-0"
                                    checked={answers[18]?.includes("D") || false}
                                    onChange={(e) => {
                                        const current = answers[18] || [];
                                        const newAnswers = [...answers];
                                        if (e.target.checked) {
                                            newAnswers[18] = [...current, "D"].slice(0, 2);
                                        } else {
                                            newAnswers[18] = current.filter(opt => opt !== "D");
                                        }
                                        setAnswers(newAnswers);
                                    }}
                                />
                                <span>cycling</span>
                            </label>

                            {/* E */}
                            <label className="flex items-center space-x-2 cursor-pointer text-black font-normal">
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 text-blue-600 bg-white border border-gray-400 rounded-sm focus:ring-0"
                                    checked={answers[18]?.includes("E") || false}
                                    onChange={(e) => {
                                        const current = answers[18] || [];
                                        const newAnswers = [...answers];
                                        if (e.target.checked) {
                                            newAnswers[18] = [...current, "E"].slice(0, 2);
                                        } else {
                                            newAnswers[18] = current.filter(opt => opt !== "E");
                                        }
                                        setAnswers(newAnswers);
                                    }}
                                />
                                <span>theatre sound and lighting</span>
                            </label>
                        </div>
                    </div>




                </div>
            </div>


            {/* Main content (scrollable) */}
            <div className="flex-1 flex flex-col items-center justify-start w-full">
                <div className="w-full px-12 py-2 flex justify-start">

                    <div ref={highlightRef} className="bg-white  shadow-sm p-6 w-full max-w-[800px] text-left overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
                        {/* Questions 21 and 22 Section */}
                        <h2 className="text-[18px] font-bold text-black mb-2">Questions 21 and 22</h2>
                        <p className="text-[14px] mb-1">Choose <strong>TWO</strong> letters, A–E.</p>
                        <p className="text-[14px] mb-4">Which TWO parts of the introductory stage to their art projects do Jess and Tom agree were useful?</p>

                        <div className="space-y-2 text-[14px] mb-6">
                            {["A", "B", "C", "D", "E"].map((option) => {
                                const labels = {
                                    A: "the Bird Park visit",
                                    B: "the workshop sessions",
                                    C: "the Natural History Museum visit",
                                    D: "the projects done in previous years",
                                    E: "the handouts with research sources",
                                };
                                return (
                                    <label key={option} className="flex items-center space-x-2 cursor-pointer text-black font-normal">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 text-blue-600 bg-white border border-gray-400 rounded-sm focus:ring-0"
                                            checked={answers[20]?.includes(option) || false}
                                            onChange={(e) => {
                                                const current = answers[20] || [];
                                                const newAnswers = [...answers];
                                                if (e.target.checked) {
                                                    newAnswers[20] = [...current, option].slice(0, 2);
                                                } else {
                                                    newAnswers[20] = current.filter(opt => opt !== option);
                                                }
                                                setAnswers(newAnswers);
                                            }}
                                            ref={el => (questionRefs.current[20] = el)}
                                        />
                                        <span>{labels[option]}</span>
                                    </label>
                                );
                            })}
                        </div>

                        {/* Questions 23 and 24 Section */}
                        <h2 className="text-[18px] font-bold text-black mb-2">Questions 23 and 24</h2>
                        <p className="text-[14px] mb-1">Choose <strong>TWO</strong> letters, A–E.</p>
                        <p className="text-[14px] mb-4">In which TWO ways do both Jess and Tom decide to change their proposals?</p>

                        <div className="space-y-2 text-[14px]">
                            {["A", "B", "C", "D", "E"].map((option) => {
                                const labels = {
                                    A: "by giving a rationale for their action plans",
                                    B: "by being less specific about the outcome",
                                    C: "by adding a video diary presentation",
                                    D: "by providing a timeline and a mind map",
                                    E: "by making their notes more evaluative",
                                };
                                return (
                                    <label key={option} className="flex items-center space-x-2 cursor-pointer text-black font-normal">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 text-blue-600 bg-white border border-gray-400 rounded-sm focus:ring-0"
                                            checked={answers[21]?.includes(option) || false}
                                            onChange={(e) => {
                                                const current = answers[21] || [];
                                                const newAnswers = [...answers];
                                                if (e.target.checked) {
                                                    newAnswers[21] = [...current, option].slice(0, 2);
                                                } else {
                                                    newAnswers[21] = current.filter(opt => opt !== option);
                                                }
                                                setAnswers(newAnswers);
                                            }}
                                            ref={el => (questionRefs.current[21] = el)}
                                        />
                                        <span>{labels[option]}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>


            {/* Main content (scrollable) */}
            <div className="flex-1 flex flex-col items-center justify-start w-full">
                <div className="w-full px-12 py-2 flex justify-start">
                    <div
                        ref={highlightRef}
                        className="bg-white shadow-sm p-6 w-full max-w-[800px] text-left overflow-y-auto"
                        style={{ maxHeight: "calc(100vh - 180px)" }}
                    >
                        {/* Heading */}
                        <h2 className="text-[17px] font-bold text-black mb-1">
                            Questions 25 – 30
                        </h2>
                        <p className="text-[13.5px] mb-0">
                            Which personal meaning do the students decide to give to each of the
                            following pictures?
                        </p>
                        <p className="text-[13.5px] mb-4">
                            Choose the correct meaning from the box and write it next to Questions
                            25–30.
                        </p>

                        <div className="grid grid-cols-2 gap-36 mb-8"> {/* reduced gap from 16 to 8 */}
                            {/* Left Column - Pictures with inputs */}
                            <div>
                                <h3 className="text-[14.5px] font-bold text-black mb-3">Pictures</h3>
                                <div className="space-y-2">
                                    {[
                                        { label: "Falcon (Landseer)", index: 24 },
                                        { label: "Fish hawk (Audubon)", index: 25 },
                                        { label: "Kingfisher (van Gogh)", index: 26 },
                                        { label: "Portrait of William Wells", index: 27 },
                                        { label: "Vairumati (Gauguin)", index: 28 },
                                        { label: "Portrait of Giovanni de Medici", index: 29 },
                                    ].map(({ label, index }) => (
                                        <div
                                            key={index}
                                            className="flex items-center gap-2 text-[13.5px] text-black"
                                        >
                                            <span
                                                className="min-w-[200px] whitespace-nowrap"
                                                title={label}
                                            >
                                                {label}
                                            </span>
                                            <span
                                                className="inline-block rounded bg-[#E0F2FE] border border-[#7DD3FC] px-1 py-[2px] shadow cursor-pointer"
                                                style={{ minWidth: 180, minHeight: 26 }}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    const droppedText = e.dataTransfer.getData("text/plain").trim();
                                                    if (!droppedText) return;

                                                    const newAnswers = [...answers];
                                                    const prevValue = newAnswers[index];

                                                    if (prevValue && prevValue !== droppedText) {
                                                        restoreToRightStack02(prevValue);
                                                    }

                                                    newAnswers[index] = droppedText;
                                                    setAnswers(newAnswers);
                                                    removeFromRightStack02(droppedText);
                                                }}
                                                onDragOver={(e) => e.preventDefault()}
                                                onClick={() => {
                                                    const currentValue = answers[index];
                                                    if (currentValue) {
                                                        restoreToRightStack02(currentValue);
                                                        const newAnswers = [...answers];
                                                        newAnswers[index] = "";
                                                        setAnswers(newAnswers);
                                                    }
                                                }}
                                                draggable={!!answers[index]}
                                                onDragStart={(e) => {
                                                    const text = answers[index];
                                                    if (text) {
                                                        e.dataTransfer.setData("text/plain", text);

                                                        const newAnswers = [...answers];
                                                        newAnswers[index] = "";
                                                        setAnswers(newAnswers);

                                                        restoreToRightStack02(text);
                                                    }
                                                }}
                                            >
                                                <div className="w-full h-full text-center text-[13px] font-semibold outline-none">
                                                    {answers[index] || <span className="text-gray-500"></span>}
                                                </div>
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Right Column - Draggable Meanings */}
                            <div
                                className="flex flex-col items-start ml-[-10px]" // negative margin to bring closer
                                onDrop={(e) => {
                                    e.preventDefault();
                                    const text = e.dataTransfer.getData("text/plain").trim();
                                    if (!text) return;

                                    restoreToRightStack02(text);
                                    const newAnswers = answers.map((ans) => (ans === text ? "" : ans));
                                    setAnswers(newAnswers);
                                }}
                                onDragOver={(e) => e.preventDefault()}
                            >
                                <h3 className="text-[13.5px] font-bold text-black mb-2 w-full text-left">
                                    Personal meanings
                                </h3>
                                <div className="flex flex-col gap-2 max-w-[220px] w-full">
                                    {availableMeanings02.map((text, idx) => (
                                        <div
                                            key={idx}
                                            draggable
                                            onDragStart={(e) => e.dataTransfer.setData("text/plain", text)}
                                            className="cursor-move inline-block rounded-lg border border-gray-300 px-2 py-1.5 shadow-sm text-[13.5px] text-black hover:bg-[#d3dce6] transition-colors max-w-max"
                                            style={{ minWidth: 80, backgroundColor: "#E3E9F0" }}
                                        >
                                            {text}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>







            {/* Bottom Navigation (fixed footer) */}
            <div className="sticky bottom-0 left-0 w-full bg-white border-t border-gray-300 z-30 flex items-center justify-between px-4 py-3">

                {/* Left: Question Buttons */}
                <div className="flex items-center space-x-1">
                    {Array.from({ length: 10 }, (_, i) => (
                        <button
                            key={i + 1}
                            onClick={() => {
                                setCurrentQuestion(i + 1);
                                setTimeout(() => {
                                    const ref = questionRefs.current[i];
                                    if (ref) {
                                        ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                        ref.focus();
                                    }
                                }, 0);
                            }}
                            className={`w-6 h-6 mx-0.5 rounded ${currentQuestion === i + 1 ? 'bg-blue-500 text-white' : 'bg-black text-white'
                                } text-[12px] font-bold flex items-center justify-center`}
                        >
                            {i + 1}
                        </button>
                    ))}
                </div>

                {/* Right: Arrows */}
                <div className="flex items-center space-x-2">
                    {/* Backward Arrow */}
                    <button
                        onClick={() => {
                            setCurrentQuestion((q) => Math.max(1, q - 1));
                            setTimeout(() => {
                                const ref = questionRefs.current[currentQuestion - 2];
                                if (ref) {
                                    ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    ref.focus();
                                }
                            }, 0);
                        }}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-[#2B7BB9] text-white hover:bg-[#2468A0] transition-colors"
                        title="Previous Question"
                        disabled={currentQuestion === 1}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    {/* Forward Arrow */}
                    <button
                        onClick={() => {
                            setCurrentQuestion((q) => Math.min(10, q + 1));
                            setTimeout(() => {
                                const ref = questionRefs.current[currentQuestion];
                                if (ref) {
                                    ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    ref.focus();
                                }
                            }, 0);
                        }}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-[#2B7BB9] text-white hover:bg-[#2468A0] transition-colors"
                        title="Next Question"
                        disabled={currentQuestion === 10}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            </div>


        </div>
    );
};

export default Listening_Class_02;
